import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Container } from "@components/ui/Container";
import { Section } from "@components/ui/Section";
import { products, productCategories } from "@data/products";
import type { Product, ProductAudience, ProductCategory } from "@app-types/index";
import { ProductIcon } from "./ProductIcon";
import styles from "./Products.module.css";

const Check = () => (
  <svg
    className={styles.check}
    width={14}
    height={14}
    viewBox="0 0 14 14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M2 7.5L5.5 11L12 3.5" />
  </svg>
);

type ProductsProps = {
  /** Render compacto para el home (sin filtros, primeros N productos populares) */
  limit?: number;
  showAllLink?: boolean;
};

export function Products({ limit, showAllLink = true }: ProductsProps) {
  // Modo home: pone primero los populares
  if (limit) {
    const items = [...products]
      .sort((a, b) => Number(!!b.popular) - Number(!!a.popular))
      .slice(0, limit);
    return (
      <Section
        id="productos"
        align="center"
        eyebrow="Nuestros productos"
        title="Coberturas pensadas para cada momento"
        description="Trabajamos con las principales aseguradoras del país. Te asesoramos para que elijas la mejor opción según tu perfil."
      >
        <Container>
          <div className={styles.grid}>
            {items.map((p) => (
              <ProductCard key={p.slug} product={p} compact />
            ))}
          </div>
          {showAllLink && (
            <div style={{ textAlign: "center", marginTop: "var(--space-10)" }}>
              <Link to="/productos" className={styles.secondaryLink}>
                Ver todos los productos →
              </Link>
            </div>
          )}
        </Container>
      </Section>
    );
  }

  // Modo página completa: con filtros
  return (
    <Section tight>
      <Container>
        <FilteredList />
      </Container>
    </Section>
  );
}

function FilteredList() {
  const [audience, setAudience] = useState<ProductAudience | "todos">("todos");
  const [category, setCategory] = useState<ProductCategory | "todas">("todas");

  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (audience !== "todos" && p.audience !== audience && p.audience !== "ambos")
        return false;
      if (category !== "todas" && p.category !== category) return false;
      return true;
    });
  }, [audience, category]);

  return (
    <>
      <div className={styles.toolbar}>
        <div className={styles.audienceGroup} role="tablist" aria-label="Audiencia">
          {(
            [
              { id: "todos", label: "Todos" },
              { id: "personas", label: "Personas" },
              { id: "empresas", label: "Empresas" },
            ] as const
          ).map((opt) => (
            <button
              key={opt.id}
              className={`${styles.audienceBtn} ${audience === opt.id ? styles.audienceBtnActive : ""}`}
              onClick={() => setAudience(opt.id)}
              role="tab"
              aria-selected={audience === opt.id}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className={styles.categoryChips} role="tablist" aria-label="Categoría">
          <button
            className={`${styles.chip} ${category === "todas" ? styles.chipActive : ""}`}
            onClick={() => setCategory("todas")}
            role="tab"
            aria-selected={category === "todas"}
          >
            Todas las categorías
          </button>
          {productCategories.map((cat) => (
            <button
              key={cat}
              className={`${styles.chip} ${category === cat ? styles.chipActive : ""}`}
              onClick={() => setCategory(cat)}
              role="tab"
              aria-selected={category === cat}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className={styles.empty}>
          <p>No encontramos productos para esa combinación.</p>
          <button
            className={styles.secondaryLink}
            onClick={() => {
              setAudience("todos");
              setCategory("todas");
            }}
          >
            Limpiar filtros →
          </button>
        </div>
      ) : (
        <div className={styles.grid}>
          {filtered.map((p) => (
            <ProductCard key={p.slug} product={p} />
          ))}
        </div>
      )}
    </>
  );
}

function ProductCard({ product, compact = false }: { product: Product; compact?: boolean }) {
  return (
    <article
      id={product.slug}
      className={`${styles.card} ${product.popular ? styles.cardPopular : ""}`}
    >
      <div className={styles.badges}>
        <span className={styles.badge}>{product.category}</span>
        {product.popular && (
          <span className={`${styles.badge} ${styles.badgePopular}`}>
            Más solicitado
          </span>
        )}
      </div>
      <div className={styles.cardHeader}>
        <div className={styles.iconBox}>
          <ProductIcon name={product.icon} size={24} />
        </div>
        <h3 className={styles.cardTitle}>{product.name}</h3>
      </div>
      <p className={styles.cardShort}>{product.short}</p>
      <ul className={styles.list}>
        {product.highlights.map((h) => (
          <li key={h}>
            <Check />
            <span>{h}</span>
          </li>
        ))}
      </ul>
      <div className={styles.cardActions}>
        <Link to={`/contacto?product=${product.slug}`} className={styles.cta}>
          Cotizar {product.name}
        </Link>
        {!compact && (
          <Link
            to={`/productos#${product.slug}`}
            className={styles.secondaryLink}
          >
            Detalles →
          </Link>
        )}
      </div>
    </article>
  );
}
